import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  ShieldCheck, 
  Users, 
  CheckCircle, 
  XCircle, 
  Trash, 
  Eye,
  Clock,
  ChartLine,
  CaretDown,
  CaretUp,
  MagnifyingGlass,
  ArrowLeft,
  Globe,
  MapPin,
  Upload,
  PaperPlaneTilt,
  X,
  FileText,
  SpinnerGap
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mocked geo data for applications by country
const GEO_DATA = [
  { country: 'Colombia', code: 'CO', applications: 2847, color: '#5B21B6', flag: '🇨🇴' },
  { country: 'Brazil', code: 'BR', applications: 2156, color: '#EC4899', flag: '🇧🇷' },
  { country: 'Mexico', code: 'MX', applications: 1489, color: '#059669', flag: '🇲🇽' },
  { country: 'Ecuador', code: 'EC', applications: 747, color: '#F59E0B', flag: '🇪🇨' },
];

const TOTAL_APPLICATIONS = 7239;

// Bulk Invite Modal Component
const BulkInviteModal = ({ isOpen, onClose, adminKey }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      setResults(null);
    } else {
      toast.error('Please select a CSV file');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a CSV file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/admin/bulk-invite`, formData, {
        headers: {
          'X-Admin-Key': adminKey,
          'Content-Type': 'multipart/form-data'
        }
      });
      setResults(response.data);
      toast.success(`Successfully sent ${response.data.successful} invites!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process bulk invites');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResults(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#5B21B6] to-[#8B5CF6] p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <PaperPlaneTilt className="w-5 h-5 text-white" weight="duotone" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Send Bulk Invites</h2>
              <p className="text-white/80 text-sm">Upload CSV to invite multiple users</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-white/80 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {!results ? (
            <>
              {/* CSV Format Info */}
              <div className="bg-[#F8F6FF] rounded-xl p-4 mb-6">
                <h3 className="text-sm font-semibold text-[#5B21B6] mb-2">CSV Format Required</h3>
                <p className="text-sm text-[#6B7280] mb-2">Your CSV file should have 3 columns:</p>
                <div className="bg-white rounded-lg p-3 font-mono text-sm text-[#1E1B4B]">
                  first_name, last_name, email<br/>
                  Maria, Garcia, maria@example.com<br/>
                  Ana, Rodriguez, ana@example.com
                </div>
              </div>

              {/* File Upload Area */}
              <div 
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                  file ? 'border-[#5B21B6] bg-[#F8F6FF]' : 'border-gray-300 hover:border-[#5B21B6] hover:bg-[#F8F6FF]'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {file ? (
                  <div className="flex flex-col items-center gap-2">
                    <FileText className="w-12 h-12 text-[#5B21B6]" weight="duotone" />
                    <p className="font-medium text-[#1E1B4B]">{file.name}</p>
                    <p className="text-sm text-[#6B7280]">{(file.size / 1024).toFixed(1)} KB</p>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setFile(null); }}
                      className="text-sm text-red-500 hover:text-red-600"
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-12 h-12 text-gray-400" weight="duotone" />
                    <p className="font-medium text-[#1E1B4B]">Click to upload CSV file</p>
                    <p className="text-sm text-[#6B7280]">or drag and drop</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Results Display */
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-[#D1FAE5] rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-[#059669]">{results.successful}</p>
                  <p className="text-sm text-[#059669]">Successful</p>
                </div>
                <div className="bg-[#FEE2E2] rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-[#DC2626]">{results.failed}</p>
                  <p className="text-sm text-[#DC2626]">Failed</p>
                </div>
                <div className="bg-[#F3F4F6] rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-[#6B7280]">{results.total}</p>
                  <p className="text-sm text-[#6B7280]">Total</p>
                </div>
              </div>

              {/* Results List */}
              <div className="border rounded-xl overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-[#6B7280]">Email</th>
                      <th className="px-4 py-2 text-left text-[#6B7280]">Status</th>
                      <th className="px-4 py-2 text-left text-[#6B7280]">Code/Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {results.results.map((result, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-[#1E1B4B]">{result.email}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            result.status === 'success' ? 'bg-green-100 text-green-700' :
                            result.status === 'skipped' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {result.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-[#6B7280] text-xs">
                          {result.invitation_code || result.reason || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-3">
          <Button variant="outline" onClick={handleClose}>
            {results ? 'Close' : 'Cancel'}
          </Button>
          {!results && (
            <Button 
              onClick={handleUpload} 
              disabled={!file || uploading}
              className="bg-gradient-to-r from-[#5B21B6] to-[#8B5CF6] hover:opacity-90"
            >
              {uploading ? (
                <>
                  <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
                  Sending Invites...
                </>
              ) : (
                <>
                  <PaperPlaneTilt className="w-4 h-4 mr-2" />
                  Send Invites
                </>
              )}
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Simple SVG Map Component for Latin America
const GeoMap = ({ data }) => {
  return (
    <div className="relative w-full h-[300px] bg-gradient-to-br from-[#F8F6FF] to-[#EDE9FE] rounded-2xl overflow-hidden">
      {/* Simplified Latin America Map SVG */}
      <svg viewBox="0 0 400 350" className="w-full h-full">
        {/* Mexico */}
        <path
          d="M50,50 L120,45 L130,80 L100,110 L60,100 Z"
          fill={data.find(d => d.code === 'MX')?.color || '#E5E7EB'}
          stroke="#fff"
          strokeWidth="2"
          className="hover:opacity-80 transition-opacity cursor-pointer"
        />
        <text x="80" y="75" fontSize="10" fill="#fff" fontWeight="bold" textAnchor="middle">MX</text>
        
        {/* Colombia */}
        <path
          d="M100,130 L150,120 L170,160 L150,200 L110,190 L90,150 Z"
          fill={data.find(d => d.code === 'CO')?.color || '#E5E7EB'}
          stroke="#fff"
          strokeWidth="2"
          className="hover:opacity-80 transition-opacity cursor-pointer"
        />
        <text x="130" y="165" fontSize="10" fill="#fff" fontWeight="bold" textAnchor="middle">CO</text>
        
        {/* Ecuador */}
        <path
          d="M80,200 L110,190 L120,220 L90,240 L70,220 Z"
          fill={data.find(d => d.code === 'EC')?.color || '#E5E7EB'}
          stroke="#fff"
          strokeWidth="2"
          className="hover:opacity-80 transition-opacity cursor-pointer"
        />
        <text x="95" y="218" fontSize="10" fill="#fff" fontWeight="bold" textAnchor="middle">EC</text>
        
        {/* Brazil */}
        <path
          d="M170,160 L280,140 L320,200 L300,280 L220,300 L150,260 L150,200 Z"
          fill={data.find(d => d.code === 'BR')?.color || '#E5E7EB'}
          stroke="#fff"
          strokeWidth="2"
          className="hover:opacity-80 transition-opacity cursor-pointer"
        />
        <text x="230" y="220" fontSize="14" fill="#fff" fontWeight="bold" textAnchor="middle">BR</text>
        
        {/* Connection lines */}
        <g stroke="#5B21B6" strokeWidth="1" strokeDasharray="4,4" opacity="0.5">
          <line x1="80" y1="75" x2="130" y2="165" />
          <line x1="130" y1="165" x2="95" y2="218" />
          <line x1="130" y1="165" x2="230" y2="220" />
        </g>
        
        {/* Pulse animations on each country */}
        <circle cx="80" cy="75" r="8" fill="#059669" opacity="0.6">
          <animate attributeName="r" values="8;15;8" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.6;0.2;0.6" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="130" cy="165" r="10" fill="#5B21B6" opacity="0.6">
          <animate attributeName="r" values="10;18;10" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.6;0.2;0.6" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="95" cy="218" r="6" fill="#F59E0B" opacity="0.6">
          <animate attributeName="r" values="6;12;6" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.6;0.2;0.6" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="230" cy="220" r="9" fill="#EC4899" opacity="0.6">
          <animate attributeName="r" values="9;16;9" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.6;0.2;0.6" dur="2s" repeatCount="indefinite" />
        </circle>
      </svg>
      
      {/* Map Title */}
      <div className="absolute top-4 left-4 flex items-center gap-2">
        <Globe className="w-5 h-5 text-[#5B21B6]" weight="duotone" />
        <span className="text-sm font-semibold text-[#1E1B4B]">Geographic Distribution</span>
      </div>
    </div>
  );
};

// Country Stats Component
const CountryStats = ({ data }) => {
  const total = data.reduce((sum, d) => sum + d.applications, 0);
  
  return (
    <div className="space-y-3">
      {data.map((country, index) => (
        <motion.div
          key={country.code}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          className="flex items-center gap-3"
        >
          <span className="text-2xl">{country.flag}</span>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-[#1E1B4B]">{country.country}</span>
              <span className="text-sm font-bold text-[#5B21B6]">{country.applications.toLocaleString()}</span>
            </div>
            <div className="h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(country.applications / total) * 100}%` }}
                transition={{ duration: 1, delay: index * 0.1 }}
                className="h-full rounded-full"
                style={{ backgroundColor: country.color }}
              />
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [storedAdminKey, setStoredAdminKey] = useState('');
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [requestDetails, setRequestDetails] = useState(null);
  const [showBulkInviteModal, setShowBulkInviteModal] = useState(false);

  const fetchData = async (key) => {
    setLoading(true);
    try {
      const [requestsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/access-requests`, {
          headers: { 'X-Admin-Key': key },
          params: filter !== 'all' ? { status: filter } : {}
        }),
        axios.get(`${API_URL}/admin/stats`, {
          headers: { 'X-Admin-Key': key }
        })
      ]);
      setRequests(requestsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error('Admin API error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && storedAdminKey) {
      fetchData(storedAdminKey);
    }
  }, [isAuthenticated, filter, storedAdminKey]);

  const handleLogin = (e) => {
    e.preventDefault();
    // Validate by making a test API call
    axios.get(`${API_URL}/admin/stats`, {
      headers: { 'X-Admin-Key': adminKey }
    }).then(() => {
      setStoredAdminKey(adminKey);
      setIsAuthenticated(true);
      toast.success('Admin access granted');
    }).catch(() => {
      toast.error('Invalid admin key');
    });
  };

  const handleApprove = async (requestId) => {
    try {
      await axios.post(`${API_URL}/admin/access-requests/${requestId}/approve`, {}, {
        headers: { 'X-Admin-Key': storedAdminKey }
      });
      toast.success('Access approved and confirmation email sent');
      fetchData(storedAdminKey);
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleReject = async (requestId) => {
    try {
      await axios.post(`${API_URL}/admin/access-requests/${requestId}/reject`, {}, {
        headers: { 'X-Admin-Key': storedAdminKey }
      });
      toast.success('Request rejected');
      fetchData(storedAdminKey);
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const handleDelete = async (requestId) => {
    if (!confirm('Are you sure you want to delete this request?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/access-requests/${requestId}`, {
        headers: { 'X-Admin-Key': storedAdminKey }
      });
      toast.success('Request deleted');
      fetchData(storedAdminKey);
      if (expandedRequest === requestId) {
        setExpandedRequest(null);
        setRequestDetails(null);
      }
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const loadRequestDetails = async (requestId) => {
    if (expandedRequest === requestId) {
      setExpandedRequest(null);
      setRequestDetails(null);
      return;
    }
    
    setExpandedRequest(requestId);
    setDetailsLoading(true);
    
    try {
      const res = await axios.get(`${API_URL}/admin/access-requests/${requestId}`, {
        headers: { 'X-Admin-Key': storedAdminKey }
      });
      setRequestDetails(res.data);
    } catch (error) {
      toast.error('Failed to load details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const filteredRequests = requests.filter(req => 
    req.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F8F6FF] flex items-center justify-center p-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="glass-card rounded-3xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#5B21B6] to-[#8B5CF6] flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-white" weight="duotone" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1E1B4B] font-['Outfit']">Admin Zone</h1>
                <p className="text-sm text-[#6B7280]">MenoPath Administration</p>
              </div>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  Admin Key
                </label>
                <Input
                  type="password"
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  placeholder="Enter admin key"
                  className="h-12 rounded-xl"
                  required
                />
              </div>
              <Button type="submit" className="w-full h-12 bg-[#5B21B6] hover:bg-[#4C1D95] rounded-xl">
                Access Admin Panel
              </Button>
            </form>
            
            <Link to="/" className="flex items-center justify-center gap-2 mt-6 text-[#6B7280] hover:text-[#5B21B6] transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F6FF]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#5B21B6] to-[#8B5CF6] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" weight="duotone" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#1E1B4B] font-['Outfit']">Admin Dashboard</h1>
              <p className="text-xs text-[#6B7280]">MenoPath Access Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => setShowBulkInviteModal(true)}
              className="bg-gradient-to-r from-[#059669] to-[#10B981] hover:opacity-90 rounded-xl"
            >
              <PaperPlaneTilt className="w-4 h-4 mr-2" />
              Send Bulk Invite
            </Button>
            <Link to="/">
              <Button variant="outline" className="rounded-xl">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Site
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Top Stats Row - Total Applications */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-6 mb-6 bg-gradient-to-r from-[#5B21B6] to-[#8B5CF6] shadow-lg"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                <Globe className="w-7 h-7 text-white" weight="duotone" />
              </div>
              <div>
                <p className="text-white/80 text-sm">Total Applications Worldwide</p>
                <p className="text-4xl font-bold text-white">{TOTAL_APPLICATIONS.toLocaleString()}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-white/80 text-sm">Growth this month</p>
              <p className="text-2xl font-bold text-[#4ADE80]">+12.5%</p>
            </div>
          </div>
        </motion.div>

        {/* Geo Map and Country Stats Row */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-2xl p-6"
          >
            <GeoMap data={GEO_DATA} />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card rounded-2xl p-6"
          >
            <div className="flex items-center gap-2 mb-6">
              <MapPin className="w-5 h-5 text-[#5B21B6]" weight="duotone" />
              <h3 className="text-lg font-semibold text-[#1E1B4B]">Applications by Country</h3>
            </div>
            <CountryStats data={GEO_DATA} />
            <div className="mt-6 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#6B7280]">Total from LATAM</span>
                <span className="font-bold text-[#1E1B4B]">{GEO_DATA.reduce((sum, d) => sum + d.applications, 0).toLocaleString()}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card rounded-2xl p-5"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#EDE9FE] flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#5B21B6]" weight="duotone" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1E1B4B]">419</p>
                  <p className="text-xs text-[#6B7280]">Total Individual Requests</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-card rounded-2xl p-5"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#DBEAFE] flex items-center justify-center">
                  <Globe className="w-5 h-5 text-[#2563EB]" weight="duotone" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1E1B4B]">9,845</p>
                  <p className="text-xs text-[#6B7280]">Requests by Organizations</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card rounded-2xl p-5"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#D1FAE5] flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#059669]" weight="duotone" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1E1B4B]">7,239</p>
                  <p className="text-xs text-[#6B7280]">Approved</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card rounded-2xl p-5"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#FEF3C7] flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#92400E]" weight="duotone" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1E1B4B]">216</p>
                  <p className="text-xs text-[#6B7280]">Pending</p>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
            <Input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 h-12 rounded-xl"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'pending', 'approved', 'rejected'].map((status) => (
              <Button
                key={status}
                variant={filter === status ? 'default' : 'outline'}
                onClick={() => setFilter(status)}
                className={`rounded-xl capitalize ${filter === status ? 'bg-[#5B21B6]' : ''}`}
              >
                {status}
              </Button>
            ))}
          </div>
        </div>

        {/* Requests Table */}
        <div className="glass-card rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F3F4F6]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#374151] uppercase tracking-wider">User</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#374151] uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#374151] uppercase tracking-wider">Registered</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#374151] uppercase tracking-wider">Prediction</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#374151] uppercase tracking-wider">Date</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-[#374151] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-[#6B7280]">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-[#5B21B6] border-t-transparent rounded-full animate-spin" />
                        Loading...
                      </div>
                    </td>
                  </tr>
                ) : filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-[#6B7280]">
                      No requests found
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((req) => (
                    <React.Fragment key={req.id}>
                      <tr className="hover:bg-[#F9FAFB] transition-colors">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-[#1E1B4B]">{req.name}</p>
                            <p className="text-sm text-[#6B7280]">{req.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            req.status === 'approved' ? 'bg-[#D1FAE5] text-[#059669]' :
                            req.status === 'rejected' ? 'bg-[#FEE2E2] text-[#DC2626]' :
                            'bg-[#FEF3C7] text-[#92400E]'
                          }`}>
                            {req.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {req.has_registered ? (
                            <CheckCircle className="w-5 h-5 text-[#059669]" weight="fill" />
                          ) : (
                            <XCircle className="w-5 h-5 text-[#D1D5DB]" weight="fill" />
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {req.has_prediction ? (
                            <CheckCircle className="w-5 h-5 text-[#059669]" weight="fill" />
                          ) : (
                            <XCircle className="w-5 h-5 text-[#D1D5DB]" weight="fill" />
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#6B7280]">
                          {formatDate(req.created_at)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => loadRequestDetails(req.id)}
                              className="p-2 hover:bg-[#EDE9FE] rounded-lg transition-colors"
                              title="View Details"
                            >
                              {expandedRequest === req.id ? (
                                <CaretUp className="w-5 h-5 text-[#5B21B6]" />
                              ) : (
                                <Eye className="w-5 h-5 text-[#5B21B6]" />
                              )}
                            </button>
                            {req.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleApprove(req.id)}
                                  className="p-2 hover:bg-[#D1FAE5] rounded-lg transition-colors"
                                  title="Approve"
                                >
                                  <CheckCircle className="w-5 h-5 text-[#059669]" />
                                </button>
                                <button
                                  onClick={() => handleReject(req.id)}
                                  className="p-2 hover:bg-[#FEE2E2] rounded-lg transition-colors"
                                  title="Reject"
                                >
                                  <XCircle className="w-5 h-5 text-[#DC2626]" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleDelete(req.id)}
                              className="p-2 hover:bg-[#FEE2E2] rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash className="w-5 h-5 text-[#DC2626]" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded Details Row */}
                      <AnimatePresence>
                        {expandedRequest === req.id && (
                          <tr>
                            <td colSpan={6} className="px-6 py-0">
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="overflow-hidden"
                              >
                                <div className="py-4 border-t border-dashed border-gray-200">
                                  {detailsLoading ? (
                                    <div className="flex items-center justify-center py-8">
                                      <div className="w-5 h-5 border-2 border-[#5B21B6] border-t-transparent rounded-full animate-spin" />
                                    </div>
                                  ) : requestDetails ? (
                                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                                      {/* Reason */}
                                      <div className="bg-[#F9FAFB] rounded-xl p-4">
                                        <h4 className="text-xs font-semibold text-[#6B7280] uppercase mb-2">Reason for Interest</h4>
                                        <p className="text-sm text-[#1E1B4B]">
                                          {requestDetails.access_request?.reason || 'Not provided'}
                                        </p>
                                      </div>
                                      
                                      {/* User Info */}
                                      <div className="bg-[#F9FAFB] rounded-xl p-4">
                                        <h4 className="text-xs font-semibold text-[#6B7280] uppercase mb-2">User Account</h4>
                                        {requestDetails.user ? (
                                          <div className="text-sm">
                                            <p className="text-[#1E1B4B]">Registered: Yes</p>
                                            <p className="text-[#6B7280]">Since: {formatDate(requestDetails.user.created_at)}</p>
                                          </div>
                                        ) : (
                                          <p className="text-sm text-[#6B7280]">Not yet registered</p>
                                        )}
                                      </div>
                                      
                                      {/* Lifestyle Data */}
                                      <div className="bg-[#F9FAFB] rounded-xl p-4">
                                        <h4 className="text-xs font-semibold text-[#6B7280] uppercase mb-2">Lifestyle Data</h4>
                                        {requestDetails.lifestyle_data ? (
                                          <div className="text-sm space-y-1">
                                            <p><span className="text-[#6B7280]">Age:</span> <span className="text-[#1E1B4B]">{requestDetails.lifestyle_data.age || 'N/A'}</span></p>
                                            <p><span className="text-[#6B7280]">BMI:</span> <span className="text-[#1E1B4B]">{requestDetails.lifestyle_data.bmi || 'N/A'}</span></p>
                                            <p><span className="text-[#6B7280]">Exercise:</span> <span className="text-[#1E1B4B]">{requestDetails.lifestyle_data.exercise_frequency || 'N/A'}</span></p>
                                          </div>
                                        ) : (
                                          <p className="text-sm text-[#6B7280]">No data submitted</p>
                                        )}
                                      </div>
                                      
                                      {/* Prediction */}
                                      <div className="bg-[#F9FAFB] rounded-xl p-4">
                                        <h4 className="text-xs font-semibold text-[#6B7280] uppercase mb-2">Prediction</h4>
                                        {requestDetails.prediction ? (
                                          <div className="text-sm space-y-1">
                                            <p><span className="text-[#6B7280]">Age Range:</span> <span className="text-[#1E1B4B] font-medium">{requestDetails.prediction.predicted_age_range}</span></p>
                                            <p><span className="text-[#6B7280]">Confidence:</span> <span className="text-[#1E1B4B]">{requestDetails.prediction.confidence}</span></p>
                                            <p><span className="text-[#6B7280]">Stage:</span> <span className="text-[#1E1B4B]">{requestDetails.prediction.stage}</span></p>
                                          </div>
                                        ) : (
                                          <p className="text-sm text-[#6B7280]">No prediction yet</p>
                                        )}
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              </motion.div>
                            </td>
                          </tr>
                        )}
                      </AnimatePresence>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
      
      {/* Bulk Invite Modal */}
      <BulkInviteModal 
        isOpen={showBulkInviteModal} 
        onClose={() => setShowBulkInviteModal(false)}
        adminKey={storedAdminKey}
      />
    </div>
  );
}
